import React from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
  Button,
  Chip,
  Stack,
  useTheme,
  alpha,
  Fade,
  Zoom,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  AddCircle as AddCircleIcon,
  TableChart as TableChartIcon,
  Article as ArticleIcon,
  Save as SaveIcon,
  CheckCircleSharp as CheckCircleSharpIcon,
  ErrorSharp as ErrorSharpIcon,
  DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { JustInfo, StyledIconButton } from "../../utils/Icon";
import { formatAmount } from "../../../utils/hooks/dataHandler";

export const StyledTableCell = ({ children, ...props }) => {
  const theme = useTheme();

  return (
    <TableCell
      {...props}
      sx={{
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
        py: 1.5,
        px: 2.5,
        position: "relative",
      }}
    >
      {children}
    </TableCell>
  );
};

export const StyledTypography = ({ children, ...props }) => {
  const theme = useTheme();

  return (
    <Typography
      {...props}
      sx={{
        color: theme.palette.text.primary,
        fontSize: "0.95rem",
        padding: "4px 8px",
      }}
    >
      {children}
    </Typography>
  );
};

export const Verification = ({
  isMatch,
  displayValue,
  matchTitle,
  notMatchTitle,
  balanceAtLeft = false,
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ gap: 1 }}>
      <Typography
        fontWeight={700}
        sx={{
          color: theme.palette.text.primary,
          fontSize: "0.95rem",
        }}
      >
        <Tooltip
          title={isMatch ? matchTitle : notMatchTitle}
          arrow
          placement='top'
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {balanceAtLeft && displayValue && (
              <span>{formatAmount(displayValue)}</span>
            )}
            <IconButton
              size='small'
              sx={{
                p: 0.5,
                "&:hover": {
                  backgroundColor: "transparent",
                  transform: "scale(1.1)",
                },
                transition: "transform 0.2s ease-in-out",
              }}
            >
              {isMatch ? (
                <CheckCircleSharpIcon
                  sx={{
                    color: theme.palette.success.main,
                    fontSize: 20,
                    filter: "drop-shadow(0 2px 4px rgba(76, 175, 80, 0.3))",
                  }}
                />
              ) : (
                <ErrorSharpIcon
                  sx={{
                    color: theme.palette.error.main,
                    fontSize: 20,
                    filter: "drop-shadow(0 2px 4px rgba(244, 67, 54, 0.3))",
                  }}
                />
              )}
            </IconButton>
            {!balanceAtLeft && displayValue && (
              <span>{formatAmount(displayValue)}</span>
            )}
          </Box>
        </Tooltip>
      </Typography>
    </Box>
  );
};

export const BalanceVerification = ({
  grossBalance,
  expectedTotal,
  balanceAtLeft = false,
  matchTitle,
  notMatchTitle,
}) => {
  const isMatch = Math.round(grossBalance) === Math.round(expectedTotal);
  const difference = expectedTotal - grossBalance;

  return (
    <Verification
      isMatch={isMatch}
      displayValue={grossBalance}
      balanceAtLeft={balanceAtLeft}
      matchTitle={matchTitle || "Amounts match – verification successful"}
      notMatchTitle={
        notMatchTitle ||
        `Difference: ${formatAmount(difference)} from expected ${formatAmount(
          expectedTotal
        )}`
      }
    />
  );
};

export const ExceedVerification = ({
  value,
  maxAmount,
  balanceAtLeft = false,
  matchTitle,
  notMatchTitle,
}) => {
  const isMatch = value <= maxAmount;

  return (
    <Verification
      isMatch={isMatch}
      displayValue={value}
      balanceAtLeft={balanceAtLeft}
      matchTitle={matchTitle || "Within Cap"}
      notMatchTitle={notMatchTitle || "Exceed Cap"}
    />
  );
};

const EmptyState = ({
  message = "Currently no data to display.",
  onAdd,
  variant = "default",
  icon: CustomIcon,
  actionLabel = "Add First Row",
  canEdit = true,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        px: 4,
        textAlign: "center",
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.grey[50],
          0.3
        )}, ${alpha(theme.palette.grey[100], 0.1)})`,
      }}
    >
      <Zoom in timeout={500}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.1
            )}, ${alpha(theme.palette.primary.main, 0.05)})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
            border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: -4,
              borderRadius: "50%",
              background: `linear-gradient(45deg, ${alpha(
                theme.palette.primary.main,
                0.2
              )}, ${alpha(theme.palette.secondary.main, 0.2)})`,
              opacity: 0.3,
              animation: "pulse 2s infinite",
            },
          }}
        >
          {CustomIcon ? (
            <CustomIcon
              sx={{ fontSize: 32, color: theme.palette.primary.main }}
            />
          ) : (
            <TableChartIcon
              sx={{ fontSize: 32, color: theme.palette.primary.main }}
            />
          )}
        </Box>
      </Zoom>

      <Fade in timeout={800}>
        <Box>
          <Typography
            variant='h6'
            color='text.primary'
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            No records found
          </Typography>

          {canEdit && onAdd && (
            <Button
              onClick={onAdd}
              variant='contained'
              startIcon={<AddIcon />}
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                boxShadow: `0 4px 16px ${alpha(
                  theme.palette.primary.main,
                  0.3
                )}`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  boxShadow: `0 6px 24px ${alpha(
                    theme.palette.primary.main,
                    0.4
                  )}`,
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {actionLabel}
            </Button>
          )}
        </Box>
      </Fade>

      <style jsx>{`
        @keyframes pulse {
          0%,
          100% {
            transform: scale(1);
            opacity: 0.3;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.1;
          }
        }
      `}</style>
    </Box>
  );
};

export const getHeaderStyles = (theme, needHeaderColor) => {
  if (needHeaderColor) {
    return {
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
      color: "white",
      "& .MuiTableCell-root": {
        color: "white",
        fontWeight: 700,
      },
    };
  }
  return {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.06
    )}, ${alpha(theme.palette.primary.main, 0.02)})`,
    "& .MuiTableCell-root": {
      color: theme.palette.primary.main,
      fontWeight: 700,
      textTransform: "uppercase",
      fontSize: "0.75rem",
      letterSpacing: "0.5px",
    },
  };
};

export const ContentHeader = ({
  theme,
  tableTitle,
  onAddRow,
  onSaveSchedule,
  tableIcon: TableIcon,
  isInfoApplicable = false,
  infoDetails = "",
  children,
  canEdit = true,
}) => {
  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        background: alpha(theme.palette.grey[50], 0.3),
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Stack direction='row' alignItems='center' spacing={2}>
          <Box
            sx={{
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.1
              )}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            {TableIcon ? (
              <TableIcon
                sx={{ color: theme.palette.primary.main, fontSize: 16 }}
              />
            ) : (
              <ArticleIcon
                sx={{ color: theme.palette.primary.main, fontSize: 16 }}
              />
            )}
          </Box>
          {tableTitle && (
            <Typography
              variant='subtitle1'
              fontWeight={600}
              color='text.primary'
            >
              {tableTitle}
            </Typography>
          )}
          {isInfoApplicable && infoDetails && infoDetails !== "" && (
            <JustInfo title={infoDetails} />
          )}
        </Stack>

        <Stack direction='row' spacing={1}>
          {children}
          {canEdit && (
            <>
              <StyledIconButton
                onClick={() => onAddRow()}
                icon={AddCircleIcon}
                tooltip='Add Row'
                iconSize='24'
              />
              <StyledIconButton
                onClick={() => onSaveSchedule()}
                icon={SaveIcon}
                tooltip='Save Schedule'
                iconSize='24'
              />
            </>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export const StickyTotalRowStyle = () => {
  return {
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    backgroundColor: (theme) => theme.palette.background.paper,
  };
};

const SortableScheduleRow = ({ id, children, sx }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <TableRow
      ref={setNodeRef}
      sx={{
        ...sx,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.85 : 1,
        position: "relative",
        zIndex: isDragging ? 5 : "auto",
      }}
    >
      <TableCell
        {...attributes}
        {...listeners}
        align='center'
        sx={{
          width: 44,
          minWidth: 44,
          maxWidth: 44,
          borderBottom: (theme) =>
            `1px solid ${alpha(theme.palette.divider, 0.05)}`,
          py: 1.5,
          px: 1,
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          color: "text.secondary",
        }}
      >
        <DragIndicatorIcon fontSize='small' />
      </TableCell>
      {children}
    </TableRow>
  );
};

const ScheduleContent = ({
  canEdit = true,
  fullScreenMode = false,
  rows = [],
  columns = [],
  noRowsHint = "No data available yet.",
  onAddRow,
  onSaveSchedule,
  needHeaderColor = false,
  children,
  variant = "default", // 'default', 'compact', 'detailed'
  showRowNumbers = false,
  showTableActions = false,
  maxHeight = 600,
  stickyHeader = true,
  striped = true,
  emptyStateIcon,
  emptyActionLabel = "Add First Row",
  tableTitle,
  tableIcon: TableIcon,
  density = "standard", // 'compact', 'standard', 'comfortable'
  canReorderRows = false,
  rowKey = "id",
  onRowsReorder,
}) => {
  const theme = useTheme();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    })
  );

  const isRowReorderEnabled =
    canEdit && canReorderRows && typeof onRowsReorder === "function";

  const getRowId = (row, index) => row?.[rowKey] ?? row?.id ?? `row-${index}`;
  const sortableItems = rows.map((row, index) => getRowId(row, index));

  const handleDragEnd = ({ active, over }) => {
    if (!active?.id || !over?.id || active.id === over.id) return;

    const oldIndex = sortableItems.findIndex((id) => id === active.id);
    const newIndex = sortableItems.findIndex((id) => id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    onRowsReorder(arrayMove(rows, oldIndex, newIndex));
  };

  const getRowHeight = () => {
    switch (density) {
      case "compact":
        return 40;
      case "comfortable":
        return 64;
      default:
        return 52;
    }
  };

  const getCellPadding = () => {
    switch (density) {
      case "compact":
        return { py: 1, px: 2 };
      case "comfortable":
        return { py: 2.5, px: 3 };
      default:
        return { py: 1.5, px: 2.5 };
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case "compact":
        return {
          boxShadow: "none",
          border: `1px solid ${theme.palette.divider}`,
        };
      case "detailed":
        return {
          background: `linear-gradient(145deg, ${
            theme.palette.background.paper
          }, ${alpha(theme.palette.primary.main, 0.01)})`,
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.06)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
        };
      default:
        return {
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.04)}`,
        };
    }
  };

  const enhancedColumns = showRowNumbers
    ? [
        {
          key: "_rowNumber",
          label: "#",
          colAlign: "center",
          headerAlign: "center",
        },
        ...columns,
      ]
    : columns;

  const tableContent = (
    <Paper
      elevation={0}
      sx={{
        ...getVariantStyles(),
        overflow: "hidden",
        minHeight: fullScreenMode ? "60vh" : "45vh",
      }}
    >
      {(tableTitle || showTableActions) && (
        <ContentHeader
          canEdit={canEdit}
          theme={theme}
          tableTitle={tableTitle}
          onAddRow={onAddRow}
          onSaveSchedule={onSaveSchedule}
          tableIcon={TableIcon}
        />
      )}

      {rows && rows.length > 0 ? (
        <TableContainer
          sx={{
            maxHeight: fullScreenMode ? maxHeight + 30 : maxHeight,
            position: "relative",
          }}
        >
          <Table
            stickyHeader={stickyHeader}
            size={density === "compact" ? "small" : "medium"}
          >
            <TableHead sx={getHeaderStyles(theme, needHeaderColor)}>
              <TableRow>
                {isRowReorderEnabled && (
                  <TableCell
                    sx={{
                      borderBottom: `2px solid ${
                        needHeaderColor
                          ? alpha("#fff", 0.2)
                          : alpha(theme.palette.primary.main, 0.1)
                      }`,
                      position: "sticky",
                      top: 0,
                      zIndex: 100,
                      backdropFilter: "blur(10px)",
                      width: 44,
                      minWidth: 44,
                      maxWidth: 44,
                    }}
                  />
                )}
                {enhancedColumns.map((col, index) => (
                  <TableCell
                    key={col.key}
                    align={col.headerAlign || "center"}
                    sx={{
                      borderBottom: `2px solid ${
                        needHeaderColor
                          ? alpha("#fff", 0.2)
                          : alpha(theme.palette.primary.main, 0.1)
                      }`,
                      position: "sticky",
                      top: 0,
                      zIndex: 100,
                      backdropFilter: "blur(10px)",
                      ...getCellPadding(),
                      ...(index === 0 && { pl: 3 }),
                      ...(index === enhancedColumns.length - 1 && { pr: 3 }),
                    }}
                  >
                    {col.key === "_rowNumber" ? "#" : col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row, index) => {
                const rowId = getRowId(row, index);
                const rowSx = {
                  minHeight: getRowHeight(),
                  ...(striped && {
                    "&:nth-of-type(even)": {
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    },
                  }),
                };
                const cells = enhancedColumns.map((col, colIndex) => (
                  <TableCell
                    key={col.key}
                    align={col.colAlign || "center"}
                    sx={{
                      borderBottom: `1px solid ${alpha(
                        theme.palette.divider,
                        0.05
                      )}`,
                      ...getCellPadding(),
                      ...(colIndex === 0 && { pl: 3 }),
                      ...(colIndex === enhancedColumns.length - 1 && {
                        pr: 3,
                      }),
                      position: "relative",
                    }}
                  >
                    {col.key === "_rowNumber" ? (
                      <Chip
                        label={index + 1}
                        size='small'
                        variant='outlined'
                        sx={{
                          minWidth: 28,
                          height: 24,
                          fontSize: "0.75rem",
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          color: theme.palette.primary.main,
                        }}
                      />
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box sx={{ flex: 1 }}>
                          {col.render ? col.render(row, index) : row[col.key]}
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                ));

                if (isRowReorderEnabled) {
                  return (
                    <SortableScheduleRow key={rowId} id={rowId} sx={rowSx}>
                      {cells}
                    </SortableScheduleRow>
                  );
                }

                return (
                  <TableRow key={rowId} id={`c_${index}`} sx={rowSx}>
                    {cells}
                  </TableRow>
                );
              })}
              {children}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState
          message={noRowsHint}
          onAdd={onAddRow}
          variant={variant}
          icon={emptyStateIcon}
          actionLabel={emptyActionLabel}
          canEdit={canEdit}
        />
      )}
    </Paper>
  );

  if (!isRowReorderEnabled || !rows?.length) return tableContent;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortableItems}
        strategy={verticalListSortingStrategy}
      >
        {tableContent}
      </SortableContext>
    </DndContext>
  );
};

export default ScheduleContent;
